/**
 * 3DT（3Dシェイプタスク）の戦略アドバイス（円形の課題定義専用）。
 *
 * ★★ ここで出す数字は**飛行支援のための参考値**であって公式スコアでは
 *   ない。公式の採点は主催者の採点ソフトが行う。UI では必ず「参考値」と
 *   分かる形で出すこと（airspace.ts と同じ方針）。
 *
 * 既存の `airspace.ts` のポリゴン式3DT採点（`AirspaceVolume`/
 * `scoreThreeD`）は変更しない。実際の3DT課題は中心+最小半径+最大半径+
 * 高度帯のドーナツ状（円形）で定義されることが多く（友人のTaskViewアプリ
 * 側の実装方針をまとめた説明ファイルのサンプルペイロード参照）、ポリゴン
 * 式より円形の方が自然に表現できるため、採点とは別枠の新しい型
 * （`ThreeDtRing`）で扱う。
 *
 * `planThreeDtRoute`のアルゴリズムは、参照した資料に厳密な指定が無い
 * （「風プロファイルを使ってリング内で長く飛べる高度遷移を探す」という
 * 目的の記述のみ）ため、境界を明示した力技探索として自前で設計した:
 * 候補となる高度層の並び（既定で最大4区間）を総当たりし、`scoreThreeD`
 * と同じ「両端がリング内にある区間だけ加算する」安全側の考え方で距離を
 * 積む。
 */

import {
  advanceAlongLayer,
  advanceThroughTransition,
  layersInBand,
  transitionSecOf,
  windLayerAtAlt,
  type LrnPoint,
  type LrnWindLayer,
} from './lrn.ts';

export interface ThreeDtRing {
  center: LrnPoint;
  rMinM: number;
  rMaxM: number;
  altMinFt: number;
  altMaxFt: number;
}

/** 点がリングの中にあるか（水平の環＋高度帯の両方を満たす） */
export function isInsideRing(ring: ThreeDtRing, p: LrnPoint & { altFt: number }): boolean {
  const r = Math.hypot(p.e - ring.center.e, p.n - ring.center.n);
  const insideHorizontal = r >= ring.rMinM && r <= ring.rMaxM;
  const insideVertical = p.altFt >= ring.altMinFt && p.altFt <= ring.altMaxFt;
  return insideHorizontal && insideVertical;
}

export interface ThreeDtStrategyOptions {
  entry: LrnPoint;
  entryAltFt: number;
  /** タスク制限時間 [秒] */
  limitSec: number;
  climbMps: number;
  descentMps: number;
  /** シミュレーションの刻み [秒]。既定30 */
  stepSec?: number;
  /** 高度切替の回数の上限（＝区間数の上限-1）。既定3（＝最大4区間） */
  maxSegments?: number;
  /**
   * 総当たりに使う候補層の数の上限。既定12。
   * 探索量は層数nに対してO(n^(maxSegments+1))なので、ここを抑えないと
   * 現実的な入力でも簡単に破綻する（下の thinLayers のコメント参照）
   */
  maxLayers?: number;
}

export interface ThreeDtSegment {
  altFt: number;
  fromSec: number;
  toSec: number;
}

/**
 * 推奨航跡の1点。地図に「リング内をどう飛ぶか」を描くために持つ。
 *
 * ★ `scoredFromPrev` は「**ひとつ前の点からこの点までの区間が
 *   `distanceInsideM` に加算されたか**」。加算の条件は区間の両端が
 *   リング内にあること（`scoreThreeD` と同じ安全側の考え方）で、
 *   高度移行中の移動は距離に数えない。地図では加算された区間だけを
 *   実線にし、それ以外と区別できるようにする。
 *   点ごとの「中にいるか」ではなく区間ごとの旗にしてあるのは、
 *   計算が実際に何を数えたかをそのまま描けるようにするため。
 */
export interface ThreeDtPathPoint extends LrnPoint {
  /** 開始からの経過時刻 [秒] */
  sec: number;
  altFt: number;
  scoredFromPrev: boolean;
}

export interface ThreeDtPlan {
  segments: ThreeDtSegment[];
  /** リング内にいた区間の水平距離の合計 [m]。これが結果（参考値） */
  distanceInsideM: number;
  /**
   * 推奨航跡（進入点から順に並ぶ）。総当たりで選ばれた最良の並びについてのみ
   * 作る。候補ごとに作ると探索量に比例して確保が増えるため
   * （既定の探索は約17,600通り）。
   */
  path: ThreeDtPathPoint[];
}

const DEFAULT_STEP_SEC = 30;
const DEFAULT_MAX_SEGMENTS = 3;
const DEFAULT_MAX_LAYERS = 12;

/**
 * 候補層を高度方向に均等に間引いて上限を掛ける。
 *
 * ★ layerSequences の生成数は層数nに対して n + n(n-1) + n(n-1)² + n(n-1)³
 *   （既定 maxSegments=3 の場合）と爆発する。71分のIGC1本から作った
 *   プロファイルで73層になり、2,700万通りの配列を確保してブラウザのタブが
 *   落ちる不具合を実機で踏んだ。本来の原因は呼び出し側が「高度×時間」の
 *   格子をそのままプロファイルとして渡していたことだが、ライブラリ側でも
 *   壊れない上限を持っておく。
 *
 * 戦略の提案に100m刻みの細かさは要らないので、帯の中から代表的な高度を
 * 均等に選ぶ（両端は必ず残す）。
 */
function thinLayers(layers: readonly LrnWindLayer[], maxLayers: number): LrnWindLayer[] {
  if (layers.length <= maxLayers) return [...layers];
  if (maxLayers <= 1) return layers.length > 0 ? [layers[0]] : [];
  const sorted = [...layers].sort((a, b) => a.altFt - b.altFt);
  const out: LrnWindLayer[] = [];
  for (let i = 0; i < maxLayers; i++) {
    out.push(sorted[Math.round((i * (sorted.length - 1)) / (maxLayers - 1))]);
  }
  return out;
}

/**
 * 長さ1〜maxLenの、候補層からの並び（連続で同じ層は選ばない。過去の層に
 * 戻るのは許可）を総当たりで作る
 */
function layerSequences(layers: readonly LrnWindLayer[], maxLen: number): LrnWindLayer[][] {
  const out: LrnWindLayer[][] = [];
  const build = (current: LrnWindLayer[]): void => {
    out.push(current);
    if (current.length >= maxLen) return;
    for (const layer of layers) {
      const prev = current[current.length - 1];
      if (prev === layer) continue;
      build([...current, layer]);
    }
  };
  for (const layer of layers) build([layer]);
  return out;
}

/**
 * 円形の3DT課題について、風プロファイルからリング内に長く留まれる
 * 高度遷移の計画を提案する。
 *
 * リングの高度帯を全くカバーしない場合は`{segments: [], distanceInsideM: 0}`
 * を返す（3DTは「入れないなりに何もしない」が常に成立する候補なので、
 * LRNの2層が物理的に不可能なケースとは性質が違い、nullにはしない）。
 */
export function planThreeDtRoute(
  ring: ThreeDtRing,
  profile: readonly LrnWindLayer[],
  options: ThreeDtStrategyOptions,
): ThreeDtPlan {
  const stepSec = options.stepSec ?? DEFAULT_STEP_SEC;
  const maxSegments = options.maxSegments ?? DEFAULT_MAX_SEGMENTS;
  const maxLayers = options.maxLayers ?? DEFAULT_MAX_LAYERS;
  const layers = thinLayers(layersInBand(profile, ring.altMinFt, ring.altMaxFt), maxLayers);

  let best: ThreeDtPlan = { segments: [], distanceInsideM: 0, path: [] };
  let bestSequence: LrnWindLayer[] | null = null;

  for (const sequence of layerSequences(layers, maxSegments + 1)) {
    const plan = simulateSequence(ring, profile, options, sequence, stepSec, false);
    if (plan.distanceInsideM > best.distanceInsideM) {
      best = plan;
      bestSequence = sequence;
    }
  }

  // 勝った並びだけ、航跡を記録しながらもう一度だけ回す。
  // 候補ごとに航跡を作ると確保量が探索量に比例して膨らむため
  // （§72 でタブが落ちた件と同じ性質の負荷を持ち込まない）
  if (bestSequence) {
    best = simulateSequence(ring, profile, options, bestSequence, stepSec, true);
  }

  return best;
}

function simulateSequence(
  ring: ThreeDtRing,
  profile: readonly LrnWindLayer[],
  options: ThreeDtStrategyOptions,
  sequence: LrnWindLayer[],
  stepSec: number,
  collectPath: boolean,
): ThreeDtPlan {
  // entryAltFtから並びの最初の層への移行と、並び内の層同士の移行、
  // すべての移行時間を先に合計する
  const transitionAlts = [options.entryAltFt, ...sequence.map((l) => l.altFt)];
  let totalTransitionSec = 0;
  for (let i = 0; i < transitionAlts.length - 1; i++) {
    totalTransitionSec += transitionSecOf(transitionAlts[i], transitionAlts[i + 1], options.climbMps, options.descentMps);
  }

  const remainingSec = options.limitSec - totalTransitionSec;
  if (remainingSec <= 0) return { segments: [], distanceInsideM: 0, path: [] };
  const flatSecPerSegment = remainingSec / sequence.length;

  const segments: ThreeDtSegment[] = [];
  const path: ThreeDtPathPoint[] = [];
  let distanceInsideM = 0;
  let pos = options.entry;
  let curAltFt = options.entryAltFt;
  let clock = 0;

  const record = (scoredFromPrev: boolean): void => {
    if (!collectPath) return;
    path.push({ e: pos.e, n: pos.n, sec: clock, altFt: curAltFt, scoredFromPrev });
  };
  // 進入点。ここより前の区間は無いので加算対象ではない
  record(false);

  for (const layer of sequence) {
    // entryAltFt（または前の層の高度）から今回の層の高度への移行。
    // 移行中の風は、直前の高度の層があればそれとの平均、無ければこの層の
    // 風だけで近似する（entryAltFtがprofile範囲外で層が引けないことがあるため）
    const prevLayer = windLayerAtAlt(profile, curAltFt) ?? layer;
    const transitionSec = transitionSecOf(curAltFt, layer.altFt, options.climbMps, options.descentMps);
    pos = advanceThroughTransition(pos, prevLayer, layer, transitionSec);
    clock += transitionSec;
    curAltFt = layer.altFt;
    // 高度移行中の移動は距離に数えない（この区間は常に加算対象外）
    record(false);

    const segFromSec = clock;
    const steps = Math.max(1, Math.ceil(flatSecPerSegment / stepSec));
    const dt = flatSecPerSegment / steps;
    for (let s = 0; s < steps; s++) {
      const before = pos;
      const beforeInside = isInsideRing(ring, { ...before, altFt: curAltFt });
      pos = advanceAlongLayer(pos, layer, dt);
      clock += dt;
      const afterInside = isInsideRing(ring, { ...pos, altFt: curAltFt });
      const scored = beforeInside && afterInside;
      if (scored) {
        distanceInsideM += Math.hypot(pos.e - before.e, pos.n - before.n);
      }
      record(scored);
    }
    segments.push({ altFt: curAltFt, fromSec: segFromSec, toSec: clock });
  }

  return { segments, distanceInsideM, path };
}
